import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import api from "../../api";
import moment from "moment";

const S = {
  page: { background: "#f5f6fa", minHeight: "100vh", color: "#1a1a2e", fontFamily: "inherit" },
  nav: { background: "#fff", padding: "14px 28px", display: "flex", alignItems: "center", gap: 16, borderBottom: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  navTitle: { fontWeight: 700, fontSize: 17, color: "#1a1a2e", letterSpacing: 0.2 },
  navBack: { color: "#6b7280", textDecoration: "none", fontSize: 14 },
  badge: { background: "#FFD200", color: "#111", fontWeight: 700, fontSize: 11, padding: "2px 9px", borderRadius: 20, letterSpacing: 0.4 },
  body: { maxWidth: 820, margin: "32px auto", padding: "0 20px" },
  card: { background: "#fff", borderRadius: 14, padding: 28, marginBottom: 28, border: "1px solid #e5e7eb", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
  cardTitle: { margin: "0 0 22px", fontSize: 15, fontWeight: 700, color: "#111827" },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 },
  textarea: { width: "100%", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, color: "#111827", padding: "10px 12px", fontSize: 14, resize: "vertical", boxSizing: "border-box", outline: "none" },
  input: { width: "100%", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, color: "#111827", padding: "10px 12px", fontSize: 14, boxSizing: "border-box", outline: "none" },
  btn: { background: "#FFD200", color: "#111", fontWeight: 700, fontSize: 14, padding: "10px 32px", borderRadius: 8, border: "none", cursor: "pointer", transition: "opacity .15s" },
  btnDisabled: { opacity: 0.55, cursor: "not-allowed" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 },
  sectionTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: "#374151" },
  count: { fontSize: 13, color: "#9ca3af", fontWeight: 500 },
  postCard: { background: "#fff", borderRadius: 10, padding: "14px 16px", marginBottom: 10, border: "1px solid #e5e7eb", display: "flex", gap: 14, alignItems: "flex-start", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  postText: { margin: "0 0 8px", fontSize: 14, color: "#111827", lineHeight: 1.6 },
  postMeta: { fontSize: 11, color: "#9ca3af" },
  delBtn: { background: "transparent", border: "none", cursor: "pointer", padding: 4, flexShrink: 0, color: "#ef4444", fontSize: 18, lineHeight: 1 },
  msgOk: { marginBottom: 12, fontSize: 13, color: "#059669", fontWeight: 500 },
  msgErr: { marginBottom: 12, fontSize: 13, color: "#ef4444", fontWeight: 500 },
  empty: { color: "#9ca3af", textAlign: "center", padding: "40px 0", fontSize: 14 },
  pgBtn: (active) => ({ background: active ? "#FFD200" : "#f3f4f6", color: active ? "#111" : "#6b7280", border: "none", borderRadius: 6, padding: "6px 13px", cursor: "pointer", fontWeight: active ? 700 : 400, fontSize: 13 }),
  audioTag: { width: "100%", marginBottom: 8, height: 36 },
};

export default function WzTradesAdmin() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: true });

  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [voice, setVoice] = useState("");

  const fetchPosts = async (p = 1) => {
    try {
      setLoader(true);
      const res = await api.getWzTradesPosts(p);
      setPosts(res.posts || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { fetchPosts(page); }, [page]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!description && !image && !voice) {
      setMsg({ text: "Add at least one field.", ok: false });
      return;
    }
    try {
      setSubmitting(true);
      setMsg({ text: "", ok: true });
      await api.createWzTradesPost({
        description: description || undefined,
        image: image || undefined,
        voice: voice || undefined,
      });
      setDescription(""); setImage(""); setVoice("");
      setMsg({ text: "Post created successfully.", ok: true });
      setPage(1);
      fetchPosts(1);
    } catch (err) {
      setMsg({ text: err.message || "Error creating post", ok: false });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.deleteWzTradesPost({ id });
      fetchPosts(page);
    } catch (err) {
      alert("Delete failed: " + (err.message || "Error"));
    }
  };

  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  return (
    <div style={S.page}>
      {/* Nav */}
      <div style={S.nav}>
        <NavLink to="/admin/post" style={S.navBack}>← Back</NavLink>
        <span style={S.navTitle}>WZ Trades Posts</span>
        <span style={S.badge}>ADMIN</span>
      </div>

      <div style={S.body}>
        {/* Create form */}
        <div style={S.card}>
          <h3 style={S.cardTitle}>Create New Post</h3>
          <form onSubmit={handleCreate}>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Text / Description</label>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Write your message..."
                style={S.textarea}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Image URL</label>
              <input
                type="text"
                value={image}
                onChange={(e) => setImage(e.target.value)}
                placeholder="https://..."
                style={S.input}
              />
              {image && (
                <img
                  src={image}
                  alt="preview"
                  style={{ maxWidth: 220, borderRadius: 8, marginTop: 10, border: "1px solid #e5e7eb" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
            </div>
            <div style={{ marginBottom: 22 }}>
              <label style={S.label}>Voice Note URL</label>
              <input
                type="text"
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                placeholder="https://..."
                style={S.input}
              />
              {voice && <audio controls src={voice} style={{ ...S.audioTag, marginTop: 10 }} />}
            </div>

            {msg.text && (
              <p style={msg.ok ? S.msgOk : S.msgErr}>{msg.text}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{ ...S.btn, ...(submitting ? S.btnDisabled : {}) }}
            >
              {submitting ? "Posting…" : "Post"}
            </button>
          </form>
        </div>

        {/* Posts list */}
        <div style={S.card}>
          <div style={S.row}>
            <h3 style={S.sectionTitle}>All Posts</h3>
            <span style={S.count}>{total} total</span>
          </div>

          {loader ? (
            <p style={S.empty}>Loading…</p>
          ) : posts.length === 0 ? (
            <p style={S.empty}>No posts yet.</p>
          ) : (
            posts.map((post) => (
              <div key={post._id} style={S.postCard}>
                <div style={{ flex: 1 }}>
                  {post.description && (
                    <p style={S.postText}>{post.description}</p>
                  )}
                  {post.image && (
                    <div style={{ marginBottom: 8 }}>
                      <img
                        src={post.image}
                        alt="post"
                        style={{ maxWidth: 200, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                  )}
                  {post.voice && (
                    <audio controls src={post.voice} style={S.audioTag} />
                  )}
                  <span style={S.postMeta}>
                    {moment(post.createdAt).format("DD MMM YYYY · HH:mm")}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(post._id)}
                  style={S.delBtn}
                  title="Delete"
                >
                  ×
                </button>
              </div>
            ))
          )}

          {totalPages > 1 && (
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 24, flexWrap: "wrap" }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} style={S.pgBtn(p === page)}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
